import { fg } from '@atlaskit/platform-feature-flags';

import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

const GQL_ASSETS_OBJECT_TYPES_NAME = 'SearchPageAssetsObjectTypesQuery';
const getAssetsObjectTypesQueryString = () => `query ${GQL_ASSETS_OBJECT_TYPES_NAME}(
	$objectTypeARIs: [ID!]!) {
		assets_objectTypesByIds(ids: $objectTypeARIs) {
    		id
    		name
    		${fg('cdm_ga') ? 'displayName' : ''}
    		sequentialId @optIn(to: "AssetsApFields")
    		icon {
      			url16
    		}
    		schema {
      			name
      			${fg('cdm_ga') ? 'displayName' : ''}
    		}
  		}
	}`;

export const assetsObjectTypesCache: {
	get: (key: string) => Promise<AggResponse<AssetsObjectTypesResponse>> | undefined;
	set: (key: string, result: Promise<AggResponse<AssetsObjectTypesResponse>>) => void;
	has: (key: string) => boolean;
	clear: (key?: string) => void;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<AssetsObjectTypesResponse>>,
		expireWhen?: ((result: AggResponse<AssetsObjectTypesResponse>) => boolean) | undefined,
	) => Promise<AggResponse<AssetsObjectTypesResponse>>;
} = inMemoryCache<AggResponse<AssetsObjectTypesResponse>>();

export type AssetsObjectTypesQueryVariables = {
	objectTypeARIs: string[];
};

export type AssetsObjectTypesResponse = {
	assets_objectTypesByIds: {
		id: string;
		name: string;
		displayName?: string | null;
		sequentialId: string;
		icon: {
			url16: string;
		};
		schema: {
			name: string;
			displayName?: string | null;
		};
	}[];
};

export const assetsObjectTypesQuery: (
	objectTypeARIs: string[],
	headers?: Record<string, string>,
) => Promise<AggResponse<AssetsObjectTypesResponse>> = async (
	objectTypeARIs: string[],
	headers?: Record<string, string>,
) => {
	return assetsObjectTypesCache.inMemoryDecorator(`${objectTypeARIs}`, async () => {
		const response = await fetchAgg<AssetsObjectTypesQueryVariables, AssetsObjectTypesResponse>({
			graphQuery: getAssetsObjectTypesQueryString(),
			variables: {
				objectTypeARIs,
			},
			operationName: GQL_ASSETS_OBJECT_TYPES_NAME,
			headers: {
				'X-ExperimentalApi': 'AssetsObjectTypes',
				...headers,
			},
		});

		const hasErrors = response.errors?.some((error) => error);

		if (hasErrors) {
			return Promise.reject(response);
		}

		return response;
	});
};
